import { Capacitor } from '@capacitor/core'
import { useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { mixpanel } from '@/mixpanel'
import { useAdminMessage } from '@/modules/healthblocks/hooks/useAdminMessage'
import { ErrorLayout } from '@/modules/shared/components/error-layout/ErrorLayout'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'
import { modeAtom, useQaClick } from '@/utils/qa-mode'

import type { FC } from 'react'

export const BlockingAdminMessagePage: FC = () => {
  // Poll for admin message every 1 minute to keep it up to date
  const { adminMessage, isLoading, error } = useAdminMessage({
    refreshInterval: 1000 * 60 * 1,
  })
  const tenantHref = useTenantHref()
  const mode = useRecoilValue(modeAtom)
  const handleQaClick = useQaClick()

  const tracked = useRef(false)
  useEffect(() => {
    if (adminMessage?.blocking && !tracked.current) {
      tracked.current = true
      mixpanel.track('blocking_admin_message_show', {
        message_type: 'maintenance',
        platform: Capacitor.getPlatform(),
      })
    }
  }, [adminMessage])

  // Only redirect away when we have a successful response confirming
  // the message is no longer blocking. On fetch errors, stay put —
  // otherwise a network blip would let users bypass the block.
  if (!isLoading && !error && (!adminMessage || !adminMessage.blocking)) {
    return <Navigate to={tenantHref('/')} replace />
  }

  if (mode === 'qa') {
    return <Navigate to={tenantHref('/')} replace />
  }

  if (!adminMessage) {
    return null
  }

  return (
    <ErrorLayout
      title={adminMessage.title ?? ''}
      message={adminMessage.message}
      markdown
      imgSrc='/img/svg/maintenance.svg'
      onTitleClick={handleQaClick}
    />
  )
}
